import { Controller } from "@hotwired/stimulus"

/**
 * SizeUniquenessController
 *
 * Provides real-time XHR uniqueness feedback for the product size form.
 * Checks whether the entered part number and size identity are already
 * in use by another size in the same product line, debouncing requests
 * to avoid hammering the server on every keystroke.
 *
 * Two checks are performed independently:
 *   1. Part number — must be unique within the product line
 *   2. Size identity — the active size type's combination of fields must
 *      be unique (nominal, sheet, tubing ID/OD, or MIL-SPEC)
 *
 * The controller reads which size type is currently active by inspecting
 * the active button managed by SizeTypeSelectorController, so both
 * controllers must be present on the same card element.
 *
 * @example
 *   <div data-controller="size-uniqueness"
 *        data-size-uniqueness-part-number-url-value="/sizes/check_part_number"
 *        data-size-uniqueness-size-url-value="/sizes/check_size"
 *        data-size-uniqueness-exclude-id-value="42">
 *     <input data-size-uniqueness-target="partNumberInput"
 *            data-action="input->size-uniqueness#checkPartNumber">
 *     <div data-size-uniqueness-target="partNumberFeedback"></div>
 *     <!-- size fields with data-action="input->size-uniqueness#checkSize" -->
 *     <div data-size-uniqueness-target="sizeFeedback"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["partNumberInput", "partNumberFeedback", "sizeFeedback",
                    "sheetWidthInput", "sheetHeightInput", "sheetDimensionsHidden"]

  static values = {
    partNumberUrl: String,
    sizeUrl: String,
    excludeId: { type: String, default: "" }
  }

  #partNumberTimer = null
  #sizeTimer = null

  disconnect() {
    clearTimeout(this.#partNumberTimer)
    clearTimeout(this.#sizeTimer)
  }

  // ============================================================
  // Public actions (wired via data-action on form fields)
  // ============================================================

  upcasePartNumber() {
    const input = this.partNumberInputTarget
    input.value = input.value.toUpperCase()
    this.checkPartNumber()
  }

  checkPartNumber() {
    clearTimeout(this.#partNumberTimer)
    const value = this.partNumberInputTarget.value.trim()

    if (!value) {
      this.#clearFeedback(this.partNumberFeedbackTarget)
      return
    }

    this.#showPending(this.partNumberFeedbackTarget)
    this.#partNumberTimer = setTimeout(() => this.#fetchPartNumber(value), 400)
  }

  buildSheetDimensions() {
    const w = this.sheetWidthInputTarget.value.trim()
    const h = this.sheetHeightInputTarget.value.trim()
    this.sheetDimensionsHiddenTarget.value = (w && h) ? `${w} x ${h}` : ""
    this.checkSize()
  }

  checkSize() {
    clearTimeout(this.#sizeTimer)
    this.#showPending(this.sizeFeedbackTarget)
    this.#sizeTimer = setTimeout(() => this.#fetchSize(), 400)
  }

  // ============================================================
  // Private
  // ============================================================

  async #fetchPartNumber(value) {
    const url = new URL(this.partNumberUrlValue, window.location.origin)
    url.searchParams.set("value", value)
    if (this.excludeIdValue) url.searchParams.set("exclude_id", this.excludeIdValue)

    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) { this.#clearFeedback(this.partNumberFeedbackTarget); return }
      const { available } = await res.json()
      this.#renderFeedback(this.partNumberFeedbackTarget, available)
    } catch {
      this.#clearFeedback(this.partNumberFeedbackTarget)
    }
  }

  async #fetchSize() {
    const type = this.#activeType()
    if (!type) { this.#clearFeedback(this.sizeFeedbackTarget); return }

    const fields = this.#activeFields(type)
    if (!fields) { this.#clearFeedback(this.sizeFeedbackTarget); return }

    const url = new URL(this.sizeUrlValue, window.location.origin)
    url.searchParams.set("type", type)
    if (this.excludeIdValue) url.searchParams.set("exclude_id", this.excludeIdValue)
    for (const [k, v] of Object.entries(fields)) {
      if (v) url.searchParams.set(k, v)
    }

    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) { this.#clearFeedback(this.sizeFeedbackTarget); return }
      const { available } = await res.json()
      this.#renderFeedback(this.sizeFeedbackTarget, available)
    } catch {
      this.#clearFeedback(this.sizeFeedbackTarget)
    }
  }

  #activeType() {
    const btn = this.element.querySelector(
      '[data-size-type-selector-target="typeButton"].list-group-item-primary'
    )
    return btn ? btn.dataset.type : null
  }

  #activeFields(type) {
    const q = (name) =>
      this.element.querySelector(`[name="product_line_size[${name}]"]`)?.value?.trim() || ""

    switch (type) {
      case "nominal": {
        const ns = q("nominal_size")
        if (!ns) return null
        return { nominal_size: ns, nominal_size_type: q("nominal_size_type") || "inches" }
      }
      case "sheet": {
        const w = this.sheetWidthInputTarget.value.trim()
        const h = this.sheetHeightInputTarget.value.trim()
        if (!w || !h) return null
        return { sheet_dimensions: `${w} x ${h}` }
      }
      case "tubing": {
        const id = q("inner_diameter")
        const od = q("outer_diameter")
        if (!id && !od) return null
        return { inner_diameter: id, outer_diameter: od }
      }
      case "milspec": {
        const ms = q("mil_spec_size")
        const mf = q("mil_spec_finish")
        if (!ms && !mf) return null
        return { mil_spec_size: ms, mil_spec_finish: mf }
      }
      default:
        return null
    }
  }

  #renderFeedback(el, available) {
    el.className = "mt-1"
    if (available) {
      el.innerHTML =
        '<small class="text-success"><i class="fa-solid fa-circle-check me-1" aria-hidden="true"></i>Available in this product line</small>'
    } else {
      el.innerHTML =
        '<small class="text-danger"><i class="fa-solid fa-circle-xmark me-1" aria-hidden="true"></i>Already in use in this product line</small>'
    }
  }

  #showPending(el) {
    el.className = "mt-1"
    el.innerHTML =
      '<small class="text-muted"><span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Checking\u2026</small>'
  }

  #clearFeedback(el) {
    el.innerHTML = ""
    el.className = ""
  }
}
